import clsx from 'clsx';
import { WHITESPACE_STATUSES, type WhitespaceStatus } from '@shared/flow/account-hierarchy-whitespace';
import { LENS_COPY, WHITESPACE_HELP, WHITESPACE_LABEL, WHITESPACE_TONE } from './hierarchyPresentation';

export interface ColorByLegendProps {
  /** Accounts per status in this hierarchy. */
  counts: Record<WhitespaceStatus, number>;
  /** Statuses currently highlighted (the legend lens); empty = no legend highlight. */
  selected: readonly WhitespaceStatus[];
  onToggle: (status: WhitespaceStatus) => void;
  onClear: () => void;
}

/**
 * The Customer status key AND a lens: each item is a toggle button (aria-pressed) that highlights
 * only that status (multi-select), disabled when no account has it. "Clear" (or Escape inside the
 * legend) resets. The swatch, label and count stay in precedence order.
 */
export function ColorByLegend({ counts, selected, onToggle, onClear }: ColorByLegendProps) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <ul className="flex flex-wrap items-center gap-1" aria-label={LENS_COPY.legendLabel} data-testid="color-by-legend">
        {WHITESPACE_STATUSES.map(status => {
          const pressed = selected.includes(status);
          return (
            <li key={status}>
              <button
                type="button"
                aria-pressed={pressed}
                disabled={counts[status] === 0}
                title={WHITESPACE_HELP[status]}
                onClick={() => onToggle(status)}
                onKeyDown={e => {
                  if (e.key !== 'Escape' || selected.length === 0) return;
                  // The first Escape only clears the lens: consume it so an enclosing full-screen
                  // dialog (document-level escape stack) does not also close. The next one closes.
                  e.stopPropagation();
                  onClear();
                }}
                className={clsx(
                  'flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs text-content-secondary',
                  'hover:bg-bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50',
                  pressed ? 'border-border-action bg-bg-selected text-content-primary' : 'border-transparent',
                )}
              >
                <span aria-hidden className={clsx('size-2.5 shrink-0 rounded-full', WHITESPACE_TONE[status].dot)} />
                <span>{WHITESPACE_LABEL[status]}</span>
                <span className="text-content-tertiary">{counts[status]}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {selected.length > 0 && (
        <button type="button" onClick={onClear} className="text-xs text-content-action hover:underline">Clear</button>
      )}
    </div>
  );
}
